/**
 * Een fout in één pagina mag nooit het hele dashboard meenemen.
 *
 * 10-09-2026. Jordy klikte als chatter op Earnings en kreeg een zwart scherm.
 * De oorzaak was React error #300 — een component die bij de tweede render
 * minder haken uitvoerde dan bij de eerste. React kan zo'n boom niet
 * herstellen en gooit ALLES weg: zijbalk, menu, alles. Eén pagina stuk, het
 * hele dashboard weg, en op het scherm geen enkele aanwijzing waarom.
 *
 * Die ene fout is gerepareerd. Deze grens is er voor de volgende: hij vangt
 * wat er onder hem omvalt, zet er een nette Engelse melding neer, en laat de
 * rest van het scherm staan. Een chatter kan dan gewoon doorwerken op de
 * pagina's die het wél doen.
 *
 * Bewust een klassecomponent: `componentDidCatch` bestaat niet als hook.
 */
import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'

interface Props {
  /** Wat er misging, in de melding. Bijvoorbeeld de paginanaam. */
  waar?: string
  children: ReactNode
}

interface State {
  fout: Error | null
}

export class Foutgrens extends Component<Props, State> {
  state: State = { fout: null }

  static getDerivedStateFromError(fout: Error): State {
    return { fout }
  }

  componentDidCatch(fout: Error, info: ErrorInfo) {
    // In de console laten we de VOLLEDIGE fout staan; die heeft een
    // ontwikkelaar nodig. Op het scherm komt alleen de korte melding.
    console.error('Error boundary caught an error', this.props.waar, fout, info)
  }

  render() {
    if (!this.state.fout) return this.props.children
    const waar = this.props.waar ? ` on ${this.props.waar}` : ''
    return (
      <div className="panel">
        <div className="head">
          <div>
            <h3>Something went wrong{waar}</h3>
            <div className="mut2">
              This section could not be displayed. The rest of the dashboard
              still works — try another page, or reload this one.
            </div>
          </div>
        </div>
        <div style={{ padding: '10px 14px' }}>
          <button className="btn sm" onClick={() => this.setState({ fout: null })}>
            Try again
          </button>
        </div>
      </div>
    )
  }
}
